import { ColumnDef } from "@tanstack/react-table";
import { ROLE_CONFIG } from "../../../../../RolesLayout/utils/constants";
import { Avatar } from "../../../../../../../../components/Avatar/Avatar";
import { TruncatedTextTooltip } from "../../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { getDateTimeByFormat } from "../../../../../../../../redux/slices/dateTimeFormatSlice";
import { UserData } from "./types";
import OrganizationDisplay from "../../../../../AllUsersLayout.tsx/components/OrganizationDisplay";

export const LoaderText = [
  "Assigning them roles",
  "Almost done, hang tight",
  "Adding a new user",
  "Assigning user role",
  "Almost done, hang tight",
];

export const createUserTableColumns = (): ColumnDef<UserData>[] => [
  {
    accessorKey: "user",
    minSize: 250,
    header: () => (
      <div className="flex items-center gap-1 py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          User Details
        </p>
      </div>
    ),
    cell: ({ row }) => {
      const user = row?.getValue("user") as UserData["user"];
      return (
        <div className="flex items-center gap-2 px-3 overflow-hidden">
          <Avatar text={user?.name} size={24} className="shrink-0" />
          <div className="flex flex-col gap-y-0.5 overflow-hidden">
            <TruncatedTextTooltip
              text={user?.name}
              maxWidth="max-w-full text-md font-medium text-verba-indigo dark:text-verba-indigo-dk leading-tight"
            >
              {user?.name}
            </TruncatedTextTooltip>
            <div className="flex items-center gap-2 overflow-hidden">
              <TruncatedTextTooltip
                text={user?.email}
                maxWidth="max-w-[160px] text-xs font-normal text-data dark:text-data-dk leading-tight"
              >
                {user?.email}
              </TruncatedTextTooltip>
              <div className="w-px h-2.5 bg-border-gray dark:bg-border-gray-dk shrink-0" />
              <TruncatedTextTooltip
                text={user?.phone}
                maxWidth="max-w-[120px] text-xs font-normal text-data dark:text-data-dk leading-tight"
              >
                {user?.phone}
              </TruncatedTextTooltip>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "role",
    size: 150,
    header: () => (
      <div className="flex items-center gap-1 py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Role
        </p>
      </div>
    ),
    cell: ({ row }) => {
      const roles = row?.getValue("role") as UserData["role"];
      const roleBgColor =
        ROLE_CONFIG[roles?.value as keyof typeof ROLE_CONFIG]?.bgColor;
      return (
        <div className="px-3">
          <div className="flex items-center gap-[5px] w-max border border-border-gray dark:border-border-gray-dk rounded-md px-1.5 py-0.5 shadow-xs-blur">
            <span className={`w-2 h-2 rounded-full shrink-0 ${roleBgColor}`} />
            <span className="text-left text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk truncate">
              {roles?.label}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "created_by",
    size: 160,
    header: () => (
      <div className="py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Created By
        </p>
      </div>
    ),
    cell: ({ row }) => {
      const createdBy = row?.getValue("created_by") as UserData["created_by"];
      return (
        <div className="px-3 text-left overflow-hidden">
          {createdBy ? (
            <span className="text-sm text-data dark:text-data-dk truncate">
              {createdBy}
            </span>
          ) : (
            <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              -
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    size: 150,
    header: () => (
      <div className="flex items-center gap-1 py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Created On
        </p>
      </div>
    ),
    cell: ({ row }) => {
      const dateTime = getDateTimeByFormat(
        new Date(row?.getValue("created_at")),
      );
      return (
        <div className="px-3 flex flex-col text-xs gap-y-1">
          <span className="text-heading-text dark:text-heading-text-dk truncate">
            {dateTime?.date}
          </span>
          <span className="text-dark-gray dark:text-dark-gray-dk truncate">
            {dateTime?.time}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "clients",
    minSize: 200,
    header: () => (
      <div className="py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Organisation
        </p>
      </div>
    ),
    cell: ({ row }) => {
      const organization = row?.getValue("clients") as UserData["clients"];
      return (
        <div className="flex items-center px-3 h-full overflow-hidden">
          <OrganizationDisplay
            organizations={organization}
            containerWidth={350}
          />
        </div>
      );
    },
  },
];
